import axiosInstance from "config/axios";
import React, { useContext, useMemo } from "react";
import {
  AuthenticationV2Service,
  CourseService,
  DocumentService,
  FacultyService,
  FacultyTypeService,
  MenuService,
  SettingService,
  SkuUserAgreementService,
} from "services/facultyPortalClient";

type GlobalRouteData = {
  summaryUrl: () => string;
  coursesUrl: () => string;
  courseDetailsUrl: (skuId?: string) => string;
  documentsUrl: () => string;
  agreementReviewUrl: (skuId?: string, skuUserAgreementId?: string) => string;
  loginCompleteUrl: () => string;
  notFoundUrl: () => string;
  logoutUrl: () => string;
  errorUrl: () => string;
};

type GlobalServices = {
  authenticationService: AuthenticationV2Service;
  settingService: SettingService;
  menuService: MenuService;
  courseService: CourseService;
  documentService: DocumentService;
  facultyTypeService: FacultyTypeService;
  skuUserAgreementService: SkuUserAgreementService;
  facultyService: FacultyService;
};

type GlobalData = GlobalRouteData & GlobalServices;

type GlobalDataProviderProps = {
  children: React.ReactNode;
};

const GlobalDataContext = React.createContext<GlobalData>({} as GlobalData);

export const GlobalDataProvider: React.FC<GlobalDataProviderProps> = ({
  children,
}: GlobalDataProviderProps) => {
  // This allows us to fetch a URL from any component using useGlobalContext()
  // We use functions in a context so that we can easily provide for internationalized URLs (/en/...), should we ever need to, based on the
  // current user's preferences

  const services = useMemo(
    () => ({
      authenticationService: new AuthenticationV2Service("", axiosInstance),
      settingService: new SettingService("", axiosInstance),
      menuService: new MenuService("", axiosInstance),
      courseService: new CourseService("", axiosInstance),
      documentService: new DocumentService("", axiosInstance),
      facultyTypeService: new FacultyTypeService("", axiosInstance),
      skuUserAgreementService: new SkuUserAgreementService("", axiosInstance),
      facultyService: new FacultyService("", axiosInstance),
    }),
    []
  );

  const routeUrls = useMemo(() => {
    return {
      summaryUrl: () => "/summary",
      coursesUrl: () => "/courses",
      courseDetailsUrl: (skuId?: string) =>
        `/courses/${skuId ?? ":skuId"}/details`,
      documentsUrl: () => "/documents",
      agreementReviewUrl: (skuId?: string, skuUserAgreementId?: string) =>
        `/agreements/review/-/${skuId ?? ":skuId"}/${
          skuUserAgreementId ?? ":skuUserAgreementId"
        }`,
      loginCompleteUrl: () => "/logincomplete",
      notFoundUrl: () => "/not-found",
      errorUrl: () => "/error",

      //asp.net fallbacks
      logoutUrl: () => "/account/logout",
    };
  }, []);

  return useMemo(
    () => (
      <GlobalDataContext.Provider value={{ ...routeUrls, ...services }}>
        {children}
      </GlobalDataContext.Provider>
    ),
    [children, services, routeUrls]
  );
};

const useGlobalContext = () => useContext<GlobalData>(GlobalDataContext);

export default useGlobalContext;
